import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

import {
    FaMapMarkerAlt,
    FaArrowRight,
    FaHotel,
    FaStar,
    FaCheck,
    FaChevronRight,
    FaCompass,
    FaShieldAlt,
} from "react-icons/fa";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";

export default function DestinationDetails() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [destination, setDestination] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDestination = async () => {
            setLoading(true);

            try {
                const res = await axios.get(
                    `https://beachhoteldubai.com/api/destinations/${slug}`
                );

                setDestination(res.data);
            } catch (err) {
                console.error("Failed to fetch destination:", err);
                setDestination(null);
            } finally {
                setLoading(false);
            }
        };

        fetchDestination();
    }, [slug]);

    const hotels = destination?.hotels || [];

    /* =====================================================
       LOADING
    ===================================================== */

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F5F0]">
                <Header />

                <div className="flex min-h-[70vh] items-center justify-center px-5">
                    <div className="text-center">

                        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#E9DDD5] border-t-[#741F2B]" />

                        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8B7774]">
                            Discovering destination...
                        </p>

                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    /* =====================================================
       NOT FOUND
    ===================================================== */

    if (!destination) {
        return (
            <div className="min-h-screen bg-[#F8F5F0]">
                <Header />

                <div className="flex min-h-[70vh] items-center justify-center px-5">

                    <div className="max-w-md text-center">

                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#E9DDD5] bg-[#F2E7E2]">
                            <FaCompass className="text-2xl text-[#741F2B]" />
                        </div>

                        <div className="mx-auto mt-6 flex items-center justify-center gap-3">
                            <span className="h-px w-7 bg-[#B99253]" />

                            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#741F2B]">
                                Dubai Marina
                            </span>

                            <span className="h-px w-7 bg-[#B99253]" />
                        </div>

                        <h1 className="mt-4 font-serif text-[34px] font-medium leading-tight tracking-[-0.02em] text-[#40151D]">
                            Destination Not Found
                        </h1>

                        <p className="mt-4 text-[13px] leading-6 text-[#82716D]">
                            We couldn't find the destination you're looking
                            for. Please explore our other Dubai destinations.
                        </p>

                        <button
                            onClick={() => navigate("/destinations")}
                            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#741F2B] px-6 py-3.5 text-[12px] font-bold text-white shadow-[0_10px_25px_rgba(116,31,43,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#54131E] hover:shadow-[0_15px_30px_rgba(116,31,43,0.25)]"
                        >
                            Explore Destinations
                            <FaArrowRight className="text-[11px]" />
                        </button>

                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F5F0] text-[#40151D]">

            {/* =====================================================
                HERO
            ===================================================== */}

            <section className="relative overflow-hidden">

                <Hero
                    heightClass="h-[300px] sm:h-[350px] md:h-[400px] lg:h-[460px]"
                    title={destination.name}
                    backgroundImage={heroImg}
                    contentClass=""
                />

                {/* Dark Burgundy Overlay */}

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#2B0A10]/25 via-[#40151D]/10 to-[#2B0A10]/65" />

                {/* Breadcrumb */}

                <div className="absolute bottom-5 left-0 right-0 z-20 sm:bottom-7">

                    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

                        <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-white/20 bg-[#40151D]/80 px-4 py-2.5 text-[10px] font-medium text-white shadow-lg backdrop-blur-md">

                            <Link
                                to="/"
                                className="transition-colors hover:text-[#E7C78B]"
                            >
                                Home
                            </Link>

                            <FaChevronRight className="text-[7px] text-white/40" />

                            <Link
                                to="/destinations"
                                className="transition-colors hover:text-[#E7C78B]"
                            >
                                Destinations
                            </Link>

                            <FaChevronRight className="text-[7px] text-white/40" />

                            <span className="font-semibold text-[#E7C78B]">
                                {destination.name}
                            </span>

                        </div>

                    </div>
                </div>

            </section>

            {/* =====================================================
                DESTINATION INTRO
            ===================================================== */}

            <section className="relative z-10 -mt-10 px-5 sm:px-8 lg:px-12 xl:px-16">

                <div className="mx-auto max-w-[1440px]">

                    <div className="overflow-hidden rounded-[24px] border border-[#E9DDD5] bg-white shadow-[0_18px_55px_rgba(64,21,29,0.09)]">

                        <div className="grid lg:grid-cols-[1fr_260px]">

                            {/* Content */}

                            <div className="p-6 sm:p-8 lg:p-11">

                                {/* Labels */}

                                <div className="flex flex-wrap items-center gap-2.5">

                                    <span className="inline-flex items-center gap-2 rounded-full border border-[#741F2B]/10 bg-[#F7EDEF] px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-[#741F2B]">
                                        <FaMapMarkerAlt className="text-[10px]" />
                                        Dubai Destination
                                    </span>

                                    <span className="inline-flex items-center gap-2 rounded-full border border-[#B99253]/25 bg-[#FBF5E9] px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-[#9B7134]">
                                        <FaHotel className="text-[10px]" />
                                        {hotels.length} Hotels
                                    </span>

                                </div>

                                {/* Heading */}

                                <h1 className="mt-5 font-serif text-[31px] font-medium leading-[1.08] tracking-[-0.02em] text-[#40151D] sm:text-[38px] lg:text-[44px]">
                                    Hotels in{" "}
                                    <span className="italic text-[#9B7134]">
                                        {destination.name}
                                    </span>
                                </h1>

                                {/* Description */}

                                <p className="mt-4 max-w-3xl text-[12px] leading-6 text-[#82716D] sm:text-[13px]">
                                    Discover comfortable and carefully selected
                                    hotels in {destination.name}. Compare
                                    accommodation options, explore hotel
                                    facilities and find the perfect stay for
                                    your Dubai trip.
                                </p>

                                {/* Trust Items */}

                                <div className="mt-7 flex flex-wrap gap-x-7 gap-y-4">

                                    <div className="flex items-center gap-2.5">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#741F2B]/10 bg-[#F7EDEF]">
                                            <FaCheck className="text-[11px] text-[#741F2B]" />
                                        </div>

                                        <div>
                                            <p className="text-[11px] font-bold text-[#40151D]">
                                                Best Hotel Deals
                                            </p>

                                            <p className="mt-0.5 text-[9px] text-[#9B8B86]">
                                                Competitive rates
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2.5">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#B99253]/20 bg-[#FBF5E9]">
                                            <FaShieldAlt className="text-[11px] text-[#9B7134]" />
                                        </div>

                                        <div>
                                            <p className="text-[11px] font-bold text-[#40151D]">
                                                Trusted Booking
                                            </p>

                                            <p className="mt-0.5 text-[9px] text-[#9B8B86]">
                                                Secure reservation
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2.5">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#741F2B]/10 bg-[#F7EDEF]">
                                            <FaHotel className="text-[11px] text-[#741F2B]" />
                                        </div>

                                        <div>
                                            <p className="text-[11px] font-bold text-[#40151D]">
                                                Quality Stays
                                            </p>

                                            <p className="mt-0.5 text-[9px] text-[#9B8B86]">
                                                Selected hotels
                                            </p>
                                        </div>
                                    </div>

                                </div>

                            </div>

                            {/* Hotel Count */}

                            <div className="flex min-h-[190px] items-center justify-center border-t border-[#E9DDD5] bg-[#F7EDEF] p-8 lg:border-l lg:border-t-0">

                                <div className="text-center">

                                    <div className="mx-auto flex h-[70px] w-[70px] items-center justify-center rounded-full border border-[#B99253]/40 bg-[#741F2B] shadow-[0_10px_25px_rgba(116,31,43,0.18)]">
                                        <FaHotel className="text-2xl text-[#E7C78B]" />
                                    </div>

                                    <p className="mt-4 font-serif text-[34px] font-medium leading-none text-[#40151D]">
                                        {hotels.length}
                                    </p>

                                    <p className="mt-2 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#82716D]">
                                        Available Hotels
                                    </p>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </section>

            {/* =====================================================
                HOTELS SECTION
            ===================================================== */}

            <section className="px-5 py-16 sm:px-8 sm:py-20 lg:px-12 lg:py-20 xl:px-16">

                <div className="mx-auto max-w-[1440px]">

                    {/* Section Intro */}

                    <div className="mb-10 flex flex-col gap-5 lg:mb-12 lg:flex-row lg:items-end lg:justify-between">

                        <div>

                            <div className="mb-3 flex items-center gap-3">
                                <span className="h-px w-8 bg-[#B99253]" />

                                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#741F2B]">
                                    Explore Your Options
                                </span>
                            </div>

                            <h2 className="font-serif text-[30px] font-medium leading-[1.08] tracking-[-0.02em] text-[#40151D] sm:text-[36px] lg:text-[42px]">
                                Find Your{" "}
                                <span className="italic text-[#9B7134]">
                                    Perfect Stay
                                </span>
                            </h2>

                        </div>

                        <p className="max-w-[440px] text-[12px] leading-6 text-[#82716D] sm:text-[13px] lg:text-right">
                            Browse our selection of hotels in{" "}
                            <span className="font-semibold text-[#741F2B]">
                                {destination.name}
                            </span>{" "}
                            and choose the stay that suits your trip.
                        </p>

                    </div>

                    {/* Hotel Grid */}

                    {hotels.length > 0 ? (

                        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">

                            {hotels.map((hotel, index) => {

                                const cleanDescription =
                                    hotel.short_description
                                        ?.replace(/<[^>]*>/g, "")
                                        ?.trim() ||
                                    "Enjoy a comfortable stay with convenient access to Dubai's attractions.";

                                const price = Math.round(
                                    Number(hotel.price_per_night || 0)
                                );

                                return (
                                    <article
                                        key={hotel.id}
                                        onClick={() =>
                                            navigate(`/hotels/${hotel.slug}`)
                                        }
                                        className="group cursor-pointer overflow-hidden rounded-[20px] border border-[#E9DDD5] bg-white shadow-[0_8px_30px_rgba(64,21,29,0.055)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_18px_45px_rgba(64,21,29,0.13)]"
                                    >

                                        {/* Image */}

                                        <div className="relative h-[245px] overflow-hidden">

                                            <img
                                                src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                                alt={hotel.name}
                                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />

                                            {/* Overlay */}

                                            <div className="absolute inset-0 bg-gradient-to-t from-[#2B0A10]/90 via-[#40151D]/10 to-transparent" />

                                            {/* Number */}

                                            <div className="absolute left-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-[#54131E]/85 text-[10px] font-bold text-[#E7C78B] backdrop-blur-md">
                                                {String(index + 1).padStart(2, "0")}
                                            </div>

                                            {/* Location */}

                                            <div className="absolute right-4 top-4">
                                                <div className="flex items-center gap-1.5 rounded-full border border-white/20 bg-[#54131E]/75 px-3 py-1.5 text-[9px] font-semibold text-white backdrop-blur-md">
                                                    <FaMapMarkerAlt className="text-[9px] text-[#E7C78B]" />

                                                    {destination.name}
                                                </div>
                                            </div>

                                            {/* Image Content */}

                                            <div className="absolute bottom-0 left-0 right-0 p-5">

                                                <div className="mb-2 flex items-center gap-1">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <FaStar
                                                            key={star}
                                                            className="text-[9px] text-[#E7C78B]"
                                                        />
                                                    ))}

                                                    <span className="ml-1 text-[9px] font-medium text-white/70">
                                                        Hotel
                                                    </span>
                                                </div>

                                                <h3 className="line-clamp-1 font-serif text-[21px] font-medium text-white">
                                                    {hotel.name}
                                                </h3>

                                            </div>

                                        </div>

                                        {/* Card Content */}

                                        <div className="p-5">

                                            <p className="line-clamp-2 min-h-[44px] text-[11px] leading-5 text-[#82716D]">
                                                {cleanDescription}
                                            </p>

                                            {/* Info */}

                                            <div className="mt-5 grid grid-cols-2 gap-3">

                                                <div className="rounded-xl border border-[#E9DDD5] bg-[#F8F5F0] p-3">

                                                    <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#9B8B86]">
                                                        Location
                                                    </p>

                                                    <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-[#741F2B]">
                                                        <FaMapMarkerAlt />

                                                        <span className="truncate">
                                                            {destination.name}
                                                        </span>
                                                    </div>

                                                </div>

                                                <div className="rounded-xl border border-[#B99253]/25 bg-[#FBF5E9] p-3">

                                                    <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#9B8B86]">
                                                        Starting From
                                                    </p>

                                                    <p className="mt-1 font-serif text-[17px] font-medium text-[#40151D]">
                                                        {price.toLocaleString()}{" "}

                                                        <span className="font-sans text-[8px] font-semibold text-[#9B7134]">
                                                            AED
                                                        </span>
                                                    </p>

                                                </div>

                                            </div>

                                            {/* CTA */}

                                            <div className="mt-5 flex items-center justify-between border-t border-[#E9DDD5] pt-4">

                                                <span className="text-[10px] font-medium text-[#9B8B86]">
                                                    View hotel details
                                                </span>

                                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#741F2B] text-white transition-all duration-300 group-hover:bg-[#B99253] group-hover:text-[#40151D]">
                                                    <FaArrowRight className="text-[11px] transition-transform duration-300 group-hover:translate-x-0.5" />
                                                </div>

                                            </div>

                                        </div>

                                        {/* Hover Line */}

                                        <div className="h-[2px] w-0 bg-[#B99253] transition-all duration-500 group-hover:w-full" />

                                    </article>
                                );
                            })}

                        </div>

                    ) : (

                        /* Empty State */

                        <div className="rounded-[22px] border border-dashed border-[#E9DDD5] bg-white px-6 py-20 text-center">

                            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#B99253]/30 bg-[#F7EDEF]">
                                <FaHotel className="text-2xl text-[#741F2B]" />
                            </div>

                            <div className="mx-auto mt-6 flex items-center justify-center gap-3">
                                <span className="h-px w-7 bg-[#B99253]" />

                                <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#741F2B]">
                                    Hotel Collection
                                </span>

                                <span className="h-px w-7 bg-[#B99253]" />
                            </div>

                            <h3 className="mt-4 font-serif text-[29px] font-medium text-[#40151D]">
                                No Hotels Found
                            </h3>

                            <p className="mx-auto mt-3 max-w-md text-[12px] leading-6 text-[#82716D]">
                                There are currently no hotels available in{" "}
                                {destination.name}. Explore other destinations
                                to find your perfect stay.
                            </p>

                            <button
                                onClick={() => navigate("/destinations")}
                                className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#741F2B] px-6 py-3.5 text-[11px] font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#54131E]"
                            >
                                Browse Destinations
                                <FaArrowRight className="text-[10px]" />
                            </button>

                        </div>

                    )}

                </div>

            </section>

            {/* =====================================================
                DESTINATION CTA
            ===================================================== */}

            <section className="px-5 pb-16 sm:px-8 lg:px-12 lg:pb-20 xl:px-16">

                <div className="relative mx-auto max-w-[1440px] overflow-hidden rounded-[24px] bg-[#54131E] px-6 py-12 sm:px-10 md:px-14 lg:py-16">

                    {/* Decoration */}

                    <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#8B2938] opacity-70 blur-3xl" />

                    <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-[#B99253] opacity-15 blur-3xl" />

                    {/* Gold Top Line */}

                    <div className="absolute left-0 top-0 h-[2px] w-full bg-[#B99253]" />

                    {/* Content */}

                    <div className="relative z-10 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">

                        <div className="max-w-2xl">

                            <div className="flex items-center gap-3">
                                <span className="h-px w-7 bg-[#B99253]" />

                                <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#E7C78B]">
                                    Your Dubai Stay Awaits
                                </p>
                            </div>

                            <h2 className="!mt-4 font-serif text-[30px] font-medium leading-[1.08] tracking-[-0.02em] !text-white sm:text-[38px] lg:text-[42px]">
                                Ready to Explore{" "}
                                <span className="italic text-[#E7C78B]">
                                    {destination.name}?
                                </span>
                            </h2>

                            <p className="mt-4 max-w-xl text-[12px] leading-6 text-white/65 sm:text-[13px]">
                                Find the right hotel for your Dubai trip and
                                enjoy a simple, trusted booking experience.
                            </p>

                        </div>

                        {/* Buttons */}

                        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">

                            <button
                                onClick={() => navigate("/hotels")}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#B99253] px-6 py-3.5 text-[11px] font-bold text-[#40151D] shadow-[0_10px_25px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#E7C78B]"
                            >
                                Explore All Hotels
                                <FaArrowRight className="text-[10px]" />
                            </button>

                            <button
                                onClick={() => navigate("/destinations")}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 text-[11px] font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10"
                            >
                                More Destinations
                            </button>

                        </div>

                    </div>

                </div>

            </section>

            <Footer />

        </div>
    );
}